const memberSetsForUnit = (unit) => {
  const sets = new Map();
  (unit.members || []).forEach((member) => {
    const memberSetSequence = member.memberSetSequence;
    const binding = {
      unitSequence: unit.unitSequence,
      memberSetSequence,
      guid: member.guid || unit.guid,
      laterality: member.laterality || unit.laterality,
      s3Bucket: member.s3Bucket || unit.s3Bucket,
      s3Key: member.s3Key || unit.s3Key,
    };
    const existing = sets.get(memberSetSequence);
    if (existing && Object.keys(binding).some((key) => binding[key] !== existing[key])) throw new Error("invalid reservation member set");
    sets.set(memberSetSequence, binding);
  });
  if (!sets.size) throw new Error("empty reservation member set");
  return Array.from(sets.values());
};

const confirmationRequest = (plan, quote) => ({
  quoteId: quote.quoteId,
  workflow: plan.apiRequest.workflow,
  billingUnitType: plan.apiRequest.billingUnitType,
  units: plan.apiRequest.units,
  confirmationToken: quote.confirmationToken,
});

const recoverablePermissionSnapshot = (snapshot) => {
  if (!snapshot || !["ADMITTED", "IN_FLIGHT"].includes(snapshot.batchState) || !Array.isArray(snapshot.permissions)) return false;
  const statesAreRecoverable = snapshot.permissions.every((permission) => {
    if (permission.memberSetState === "RESERVED") {
      return permission.permissionId === null && permission.permissionExpiresAt === null && permission.permissionRevokedAt === null;
    }
    return permission.memberSetState === "AUTHORIZED" && Boolean(permission.permissionId) &&
      Boolean(permission.permissionExpiresAt) && permission.permissionRevokedAt === null;
  });
  const allAuthorized = snapshot.permissions.every((permission) => permission.memberSetState === "AUTHORIZED");
  return statesAreRecoverable && snapshot.uploadEligible === allAuthorized;
};

const isUncertainOutcome = (error) => error?.code === "RESERVATION_OUTCOME_UNKNOWN" || error?.retryable === true;

export const reconcilePostConfirmFailure = async ({ reservationClient, reservationId, error }) => {
  if (!error.reservationId) error.reservationId = reservationId;
  if (isUncertainOutcome(error) || typeof reservationClient.cancelReservation !== "function") return error;
  try {
    error.cancellation = await reservationClient.cancelReservation({ reservationId });
  } catch (cancellationError) {
    error.cancellation = cancellationError;
  }
  return error;
};

export const persistReservationRowsOrCancel = async ({ reservationClient, reservationId, rows, persistRows } = {}) => {
  if (typeof persistRows !== "function") throw new Error("reservation row persistence is unavailable");
  try {
    const result = await persistRows(rows);
    const middlewareValue = result && Object.prototype.hasOwnProperty.call(result, "value")
      ? result.value
      : result;
    const persisted = middlewareValue && middlewareValue.data
      ? middlewareValue.data
      : middlewareValue;
    const guids = new Set((persisted || []).map((row) => row && row.guid).filter(Boolean));
    const expected = new Set((rows || []).map((row) => row && row.guid).filter(Boolean));
    if (expected.size !== rows.length || Array.from(expected).some((guid) => !guids.has(guid))) {
      const error = new Error("reservation row persistence is incomplete");
      error.code = "PERSISTENCE_INCOMPLETE";
      throw error;
    }
    return result;
  } catch (persistenceError) {
    persistenceError.phase = "initial-persistence";
    throw await reconcilePostConfirmFailure({ reservationClient, reservationId, error: persistenceError });
  }
};

export const requiresEntitlementRefresh = (error) => Boolean(
  error && (error.code === "RESERVATION_OUTCOME_UNKNOWN" || error.reservationId || error.cancellation)
);

export const mapReservationRowsOrCancel = async ({ reservationClient, reservationId, mapRows } = {}) => {
  if (!reservationClient || typeof reservationClient.cancelReservation !== "function" || typeof mapRows !== "function") {
    throw new Error("reservation row mapping is unavailable");
  }
  try {
    return mapRows();
  } catch (mappingError) {
    throw await reconcilePostConfirmFailure({ reservationClient, reservationId, error: mappingError });
  }
};

export const createBulkReservationAdmission = ({ reservationClient } = {}) => {
  if (!reservationClient || typeof reservationClient.createQuote !== "function" || typeof reservationClient.confirmReservation !== "function") {
    throw new Error("reservation client is required");
  }

  const plannedSetsFor = (plan) => {
    if (!plan || !Array.isArray(plan.units)) throw new Error("reservation plan is required");
    return plan.units.map((planUnit) => ({ planUnit, sets: memberSetsForUnit(planUnit) }));
  };

  const issuePlannedPermissions = async ({ plannedSets, reservation }) => {
    const units = [];
    try {
      for (const { planUnit, sets } of plannedSets) {
        const memberSets = [];
        for (const request of sets) {
          const issued = await reservationClient.issuePermission({ reservationId: reservation.reservationId, request });
          memberSets.push({ memberSetSequence: request.memberSetSequence, permission: issued.data });
        }
        units.push({ unitSequence: planUnit.unitSequence, planUnit, memberSets });
      }
    } catch (permissionError) {
      throw await reconcilePostConfirmFailure({ reservationClient, reservationId: reservation.reservationId, error: permissionError });
    }
    return { reservation, units, terminal: false };
  };

  const recover = async ({ plan, reservationId, reservation: knownReservation } = {}) => {
    if (typeof reservationClient.getReservation !== "function" || typeof reservationClient.getPermissions !== "function" ||
      typeof reservationClient.issuePermission !== "function") throw new Error("reservation recovery is unavailable");
    const plannedSets = plannedSetsFor(plan);
    const expectedMemberSets = plannedSets.flatMap(({ sets }) => sets.map(({ unitSequence, memberSetSequence }) => ({ unitSequence, memberSetSequence })));
    const reservation = knownReservation || await reservationClient.getReservation({ reservationId });
    const permissions = await reservationClient.getPermissions({ reservationId, expectedMemberSets });
    if (!["reserved", "processing"].includes(reservation.state)) return { reservation, units: [], terminal: true };
    if (!recoverablePermissionSnapshot(permissions)) throw new Error("reservation is not upload eligible");
    return issuePlannedPermissions({ plannedSets, reservation });
  };

  return {
    quote(plan) {
      if (!plan || !plan.apiRequest) throw new Error("reservation plan is required");
      if (!Array.isArray(plan.apiRequest.units) || plan.apiRequest.units.length < 1) throw new Error("empty reservation plan");
      return reservationClient.createQuote(plan.apiRequest);
    },

    async confirm({ plan, quote, idempotencyKey, onStateChange } = {}) {
      if (!plan || !quote || typeof reservationClient.issuePermission !== "function") throw new Error("reservation confirmation is invalid");
      const confirmation = await reservationClient.confirmReservation({
        request: confirmationRequest(plan, quote),
        idempotencyKey,
      });
      const reservation = confirmation.data;
      const plannedSets = plannedSetsFor(plan);
      if (typeof onStateChange === "function") {
        try {
          onStateChange({ state: confirmation.replayed ? "replay-awaiting-permission" : "confirmed", reservation });
        } catch (ignored) {
          // UI observation cannot change the durable confirmation outcome.
        }
      }

      if (confirmation.replayed) {
        const recovered = await recover({ plan, reservationId: reservation.reservationId });
        if (recovered.terminal) throw new Error("reservation is not upload eligible");
        return { ...recovered, reservation };
      }

      const issued = await issuePlannedPermissions({ plannedSets, reservation });
      return { reservation: issued.reservation, units: issued.units };
    },

    recover,
  };
};
